import { computed, makeObservable } from 'mobx'
import { MeetingResultsSlide } from './MeetingResultsSlide'
import { sortQuestions } from '../../../sources-of-truth/sortQuestions'
import { StudentPeerReviewController } from '../../StudentAssignment/StudentPeerReviewController'

type EndOfSessionQuizPhase =
  | 'loading'
  | 'quiz'
  | 'peerReview'
  | 'processing'
  | 'results'

export class EndOfSessionQuizSlide extends MeetingResultsSlide {
  private peerReviewController?: StudentPeerReviewController

  afterConstructor() {
    makeObservable(this, {
      questions: computed,
      phase: computed,
    })
  }

  private getPeerReviewController(): StudentPeerReviewController | undefined {
    const roomState = this.meeting.roomState
    if (
      !roomState.isLoaded ||
      !roomState.data.sectionId ||
      !roomState.data.assignmentId
    ) {
      return undefined
    }

    if (!this.peerReviewController) {
      this.peerReviewController = new StudentPeerReviewController(
        this.meeting.repository,
        {
          roomId: roomState.id,
          userId: this.meeting.repository.uid,
          sectionId: roomState.data.sectionId,
          assignmentId: roomState.data.assignmentId,
          peerReviewExcludesSelfReview:
            this.meeting.assignment.data.peerReviewExcludesSelfReview ?? false,
          userIdsInRoom: roomState.userIds,
        }
      )
      this.peerReviewController.initializeAsReviewer()
    }

    return this.peerReviewController
  }

  get questions() {
    const endOfSessionQuestions = this.meeting.questions.filter(
      (q) => q.slideId === this.meeting.currentSlide?.id && q.isQuiz
    )
    return sortQuestions({ questions: endOfSessionQuestions, slideIds: [] })
  }

  get titleIndex(): number {
    // loop over currentSlide.questions and find out if all are submitted
    for (const q of this.questions) {
      if (!q.isSubmitted) {
        return 0
      }
    }
    return 1
  }

  get canProceed() {
    return (
      this.meeting.endOfSessionQuizSlideCanProceed || this.meeting.hasFeedback
    )
  }

  get peerReviewControllerForView(): StudentPeerReviewController | undefined {
    return this.getPeerReviewController()
  }

  get phase(): EndOfSessionQuizPhase {
    if (
      this.meeting.slideDeckQuestions.isLoading ||
      this.peerReviewControllerForView?.userYetToCompletePeerReview.isLoading
    )
      return 'loading'

    const allAnswered = this.questions.every(
      (question) => question.isAnswered && question.isSubmitted
    )

    if (this.questions.length > 0 && !allAnswered) return 'quiz'

    if (this.questions.length === 0 || allAnswered) {
      if (this.peerReviewControllerForView?.userYetToCompletePeerReview.value) {
        return 'peerReview'
      }

      if (this.meeting.endOfSessionQuizSlideShouldProceed) return 'results'
      // if someone has proceeded, go to results, otherwise go to processing
      return 'processing'
    }

    return 'results'
  }
}
